import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { openInExternalBrowser } from "@/lib/platform";

interface FooterLinksMenuProps {
  onOpenMetricsTour?: () => void;
  onSignOut?: () => void;
}

export const FooterLinksMenu = ({ onOpenMetricsTour, onSignOut }: FooterLinksMenuProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const menuItems = [
    { emoji: "⚡", label: t('nav.upgrade'), onClick: () => navigate("/upgrade") },
    { emoji: "⚙️", label: t('nav.settings'), onClick: () => navigate("/settings") },
    { emoji: "🐛", label: t('nav.report_bug'), onClick: () => navigate("/report-bug") },
    { emoji: "📧", label: t('nav.contact_us'), onClick: () => navigate("/contact-us") },
    { emoji: "🔒", label: t('nav.privacy_policy'), onClick: () => navigate("/privacy-policy") },
    { emoji: "📜", label: t('nav.terms_conditions'), onClick: () => navigate("/terms-and-conditions") },
    // { emoji: "🎓", label: "Tutorial", onClick: () => navigate("/tutorial") }, // Disabled for now
    ...(onOpenMetricsTour ? [{ emoji: "📊", label: t('nav.metrics_tour'), onClick: onOpenMetricsTour }] : []),
    { emoji: "ℹ️", label: t('nav.about'), onClick: () => navigate("/about") },
    { emoji: "🔬", label: t('nav.research'), onClick: () => navigate("/proof", { state: { fromMenu: true } }) },
    {
      emoji: "💬",
      label: t('nav.discord'),
      onClick: () => openInExternalBrowser("https://discord.gg/FaYD2mJFhW")
    },
    ...(onSignOut ? [{ emoji: "🚪", label: t('nav.sign_out'), onClick: onSignOut }] : []),
  ];

  const handleItemClick = (onClick: () => void) => {
    onClick();
    setOpen(false);
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="sm" title={t('nav.menu')}>
          <Menu className="w-4 h-4" />
        </Button>
      </SheetTrigger>
      <SheetContent side="top" className="max-h-[90vh] overflow-y-auto">
        <SheetHeader className="text-left">
          <SheetTitle className="text-left">{t('nav.links')}</SheetTitle>
        </SheetHeader>
        <div className="mt-4 flex flex-col gap-1 pb-6">
          {menuItems.map((item, index) => (
            <Button
              key={index}
              variant="ghost"
              className="justify-start text-left h-auto py-3 px-3 font-normal"
              onClick={() => handleItemClick(item.onClick)}
            >
              <span className="mr-3 text-lg">{item.emoji}</span>
              <span className="text-sm opacity-75">{item.label}</span>
            </Button>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
};
